import apiClient from "@/api/axiosClient";
import type {
  Asset, AssetQueryParams, PagedResult, AssignmentHistoryEntry, EmployeeProfile,
  AssetDocument, AssetDocumentType, AssetCategorySummary, AssetStatus,
  GenerateAssetCodesPayload, GenerateAssetCodesResult,
  CreateAssetsFromCodesPayload, GenerateNewAssetsWithCodesPayload, AssetCodeBatchResult
} from "@/types/asset";
import type { AssetFormValues } from "@/schemas/assetSchema";

export async function fetchAssets(params: AssetQueryParams): Promise<PagedResult<Asset>> {
  const { data } = await apiClient.get<PagedResult<Asset>>("/assets", { params });
  return data;
}

export async function fetchAssetCategorySummary(): Promise<AssetCategorySummary[]> {
  const { data } = await apiClient.get<AssetCategorySummary[]>("/assets/category-summary");
  return data;
}

export async function fetchAssetById(id: string): Promise<Asset> {
  const { data } = await apiClient.get<Asset>(`/assets/${id}`);
  return data;
}

export async function updateAsset(id: string, values: AssetFormValues): Promise<Asset> {
  const { data } = await apiClient.put<Asset>(`/assets/${id}`, values);
  return data;
}

export async function deleteAsset(id: string): Promise<void> {
  await apiClient.delete(`/assets/${id}`);
}

export async function bulkDeleteAssets(ids: string[]): Promise<void> {
  await apiClient.post("/assets/bulk-delete", ids);
}

export async function bulkUpdateAssetStatus(ids: string[], status: AssetStatus): Promise<void> {
  await apiClient.post("/assets/bulk-update-status", { ids, status });
}

export async function generateAssetCodes(payload: GenerateAssetCodesPayload): Promise<GenerateAssetCodesResult> {
  const { data } = await apiClient.post<GenerateAssetCodesResult>("/assets/generate-codes", payload);
  return data;
}

export async function createAssetsFromCodes(payload: CreateAssetsFromCodesPayload): Promise<AssetCodeBatchResult> {
  const { data } = await apiClient.post<AssetCodeBatchResult>("/assets/create-from-codes", payload);
  return data;
}

export async function generateNewAssetsWithCodes(payload: GenerateNewAssetsWithCodesPayload): Promise<AssetCodeBatchResult> {
  const { data } = await apiClient.post<AssetCodeBatchResult>("/assets/generate-new-with-codes", payload);
  return data;
}

export async function lookupAsset(value: string): Promise<Asset> {
  const { data } = await apiClient.get<Asset>(`/assets/lookup/${encodeURIComponent(value)}`);
  return data;
}

export async function fetchAssignmentHistory(assetId: string): Promise<AssignmentHistoryEntry[]> {
  const { data } = await apiClient.get<AssignmentHistoryEntry[]>(`/assignments/asset/${assetId}/history`);
  return data;
}

export async function searchEmployees(query: string): Promise<EmployeeProfile[]> {
  const { data } = await apiClient.get<EmployeeProfile[]>("/users/search", { params: { q: query } });
  return data;
}

export async function assignAsset(payload: {
  assetId: string;
  targetAzureObjectId: string;
  expectedReturnDate?: string;
  locationId?: string;
  remarks?: string;
}) {
  const { data } = await apiClient.post("/assignments/assign", payload);
  return data;
}

export async function transferAsset(payload: {
  assetId: string;
  newTargetAzureObjectId: string;
  locationId?: string;
  remarks?: string;
}) {
  const { data } = await apiClient.post("/assignments/transfer", payload);
  return data;
}

export async function returnAsset(payload: { assetId: string; conditionAfter?: string; locationId?: string; remarks?: string }) {
  const { data } = await apiClient.post("/assignments/return", payload);
  return data;
}

export async function uploadAssetDocument(
  assetId: string,
  file: File,
  documentType: AssetDocumentType
): Promise<AssetDocument> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("documentType", documentType);
  const { data } = await apiClient.post<AssetDocument>(`/assets/${assetId}/documents`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data;
}

export async function deleteAssetDocument(assetId: string, documentId: string): Promise<void> {
  await apiClient.delete(`/assets/${assetId}/documents/${documentId}`);
}

/** Downloads the file as a Blob - documents require an auth header, so a plain <a href> or <img src> can't fetch them. */
export async function fetchAssetDocumentBlob(assetId: string, documentId: string): Promise<Blob> {
  const { data } = await apiClient.get(`/assets/${assetId}/documents/${documentId}/download`, { responseType: "blob" });
  return data;
}
